// ─────────────────────────────────────────────────────────────────────────────
// MonthlyCalendar — Phase 8 Pass D maturation
//
// Improvements over v1:
//   1. Toggleable metric — squares can show: Net P&L, Trade Count, Win Rate,
//      Avg Trade Size, Discipline Score
//   2. Streak panel — current streak, longest win/loss streak, best/worst day
//   3. Click day → trade list — expanded day panel shows actual trade rows
//   4. Detector-flagged days — days with trades in the Hour Cliff worst slot
//      get a small ⚠ indicator (passed via optional detectorResults prop)
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { C } from "../../lib/colors";
import { fmt$, tradeAmount } from "../../lib/format";

const METRICS = [
  { id: "pnl",        label: "Net P&L",          shortLabel: "P&L" },
  { id: "count",      label: "Trade Count",       shortLabel: "Trades" },
  { id: "winrate",    label: "Win Rate",          shortLabel: "WR%" },
  { id: "avgsize",    label: "Avg Position Size", shortLabel: "Size" },
  { id: "discipline", label: "Discipline Score",  shortLabel: "Disc." },
];

function getDayMetricValue(dayData, metric) {
  if (!dayData || !dayData.trades.length) return null;
  const { trades, pnl } = dayData;
  switch (metric) {
    case "pnl": return pnl;
    case "count": return trades.length;
    case "winrate": {
      const d = trades.filter(t => t.result === "Win" || t.result === "Loss");
      if (!d.length) return null;
      return trades.filter(t => t.result === "Win").length / d.length;
    }
    case "avgsize": {
      const s = trades.filter(t => t.position_size != null);
      if (!s.length) return null;
      return s.reduce((a, t) => a + Number(t.position_size || 0), 0) / s.length;
    }
    case "discipline": {
      const s = trades.filter(t => t.discipline_score != null);
      if (!s.length) return null;
      return s.reduce((a, t) => a + Number(t.discipline_score || 0), 0) / s.length;
    }
    default: return null;
  }
}

function formatMetric(value, metric) {
  if (value == null) return null;
  switch (metric) {
    case "pnl": return fmt$(value);
    case "count": return String(value);
    case "winrate": return `${Math.round(value * 100)}%`;
    case "avgsize": return value.toFixed(1);
    case "discipline": return value.toFixed(1);
    default: return String(value);
  }
}

function getSquareColor(value, metric) {
  if (value == null) return { bg: C.border + "10", border: C.border + "40" };
  switch (metric) {
    case "pnl":
      return value > 0
        ? { bg: C.green + "18", border: C.green + "60" }
        : { bg: C.red + "18", border: C.red + "60" };
    case "count":
      return { bg: C.blue + "15", border: C.blue + "40" };
    case "winrate":
      return value >= 0.6
        ? { bg: C.green + "18", border: C.green + "60" }
        : value >= 0.4
        ? { bg: C.yellow + "18", border: C.yellow + "60" }
        : { bg: C.red + "18", border: C.red + "60" };
    case "avgsize":
      return { bg: C.purple + "15", border: C.purple + "40" };
    case "discipline":
      return value >= 7
        ? { bg: C.green + "18", border: C.green + "60" }
        : value >= 5
        ? { bg: C.yellow + "18", border: C.yellow + "60" }
        : { bg: C.red + "18", border: C.red + "60" };
    default:
      return { bg: C.border + "10", border: C.border + "40" };
  }
}

function StatTile({ label, value, sub, color }) {
  return (
    <div style={{ padding: "10px 12px", background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8 }}>
      <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color, fontFamily: "monospace" }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function MiniTile({ label, value, color }) {
  return (
    <div style={{ padding: "8px 10px", background: "#0b0d19", borderRadius: 6, textAlign: "center" }}>
      <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color, fontFamily: "monospace" }}>{value}</div>
    </div>
  );
}

function StreakPanel({ dayTradeMap, daysInMonth, currentMonth }) {
  const stats = useMemo(() => {
    const tradingDays = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const data = dayTradeMap[d];
      if (!data || !data.trades.length) continue;
      tradingDays.push({ day: d, pnl: data.pnl, trades: data.trades.length });
    }
    if (!tradingDays.length) return null;

    const today = new Date();
    const isCurrentMonth = currentMonth.getMonth() === today.getMonth()
      && currentMonth.getFullYear() === today.getFullYear();
    const lastDay = isCurrentMonth ? today.getDate() : daysInMonth;

    let currentStreak = 0;
    let currentStreakType = null;
    for (let i = tradingDays.length - 1; i >= 0; i--) {
      if (tradingDays[i].day > lastDay) continue;
      const type = tradingDays[i].pnl >= 0 ? "win" : "loss";
      if (currentStreakType === null) { currentStreakType = type; currentStreak = 1; }
      else if (type === currentStreakType) { currentStreak++; }
      else break;
    }

    let longestWin = 0, longestLoss = 0, curWin = 0, curLoss = 0;
    for (const d of tradingDays) {
      if (d.pnl >= 0) { curWin++; curLoss = 0; longestWin = Math.max(longestWin, curWin); }
      else            { curLoss++; curWin = 0;  longestLoss = Math.max(longestLoss, curLoss); }
    }

    const sorted = [...tradingDays].sort((a, b) => b.pnl - a.pnl);
    const best  = sorted[0];
    const worst = sorted[sorted.length - 1];
    const totalPnl = tradingDays.reduce((s, d) => s + d.pnl, 0);

    return { currentStreak, currentStreakType, longestWin, longestLoss, best, worst, totalPnl, tradingDayCount: tradingDays.length };
  }, [dayTradeMap, daysInMonth, currentMonth]);

  if (!stats) return null;

  const monthName = currentMonth.toLocaleString("en-US", { month: "long" });

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, marginTop: 16 }}>
      <StatTile
        label="Current Streak"
        value={`${stats.currentStreak} ${stats.currentStreakType === "win" ? "🟢" : "🔴"}`}
        sub={stats.currentStreakType === "win" ? "winning days" : "losing days"}
        color={stats.currentStreakType === "win" ? C.green : C.red}
      />
      <StatTile label="Best Win Streak"   value={`${stats.longestWin}d`}  sub={`in ${monthName}`} color={C.green} />
      <StatTile label="Worst Loss Streak" value={`${stats.longestLoss}d`} sub={`in ${monthName}`} color={stats.longestLoss >= 3 ? C.red : C.muted} />
      {stats.best && <StatTile label="Best Day"  value={fmt$(stats.best.pnl)}  sub={`${monthName} ${stats.best.day}`}  color={C.green} />}
      {stats.worst && stats.worst.pnl < 0 && <StatTile label="Worst Day" value={fmt$(stats.worst.pnl)} sub={`${monthName} ${stats.worst.day}`} color={C.red} />}
      <StatTile label="Trading Days" value={String(stats.tradingDayCount)} sub={`avg ${fmt$(stats.totalPnl / stats.tradingDayCount)}/day`} color={C.text} />
    </div>
  );
}

function DayDetail({ day, currentMonth, dayTradeMap, dayNotes, editingNote, setEditingNote, setDayNotes, handleSaveNote }) {
  const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
  const dateLabel = date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const data = dayTradeMap[day];

  return (
    <div style={{ marginTop: 16, padding: 16, background: C.border + "20", borderRadius: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 12 }}>{dateLabel}</div>

      {data && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: 8, marginBottom: 14 }}>
          <MiniTile label="Trades" value={data.trades.length} color={C.text} />
          <MiniTile label="Net P&L" value={fmt$(data.pnl)} color={data.pnl >= 0 ? C.green : C.red} />
          {(() => {
            const decisive = data.trades.filter(t => t.result === "Win" || t.result === "Loss");
            const wr = decisive.length ? Math.round(data.trades.filter(t => t.result === "Win").length / decisive.length * 100) : null;
            return wr != null ? <MiniTile label="Win Rate" value={`${wr}%`} color={wr >= 50 ? C.green : C.red} /> : null;
          })()}
          {(() => {
            const scored = data.trades.filter(t => t.discipline_score != null);
            if (!scored.length) return null;
            const avg = scored.reduce((s, t) => s + Number(t.discipline_score), 0) / scored.length;
            return <MiniTile label="Avg Disc." value={avg.toFixed(1)} color={avg >= 7 ? C.green : avg >= 5 ? C.yellow : C.red} />;
          })()}
        </div>
      )}

      {data && data.trades.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Trades</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {[...data.trades]
              .sort((a, b) => (a.entry_time || "").localeCompare(b.entry_time || ""))
              .map((t, i) => {
                const net = (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0);
                return (
                  <div key={t.id || i} style={{
                    display: "grid", gridTemplateColumns: "52px 60px 38px 1fr 78px",
                    gap: 8, alignItems: "center", fontSize: 12, padding: "6px 10px",
                    background: "#0b0d19", borderRadius: 6,
                  }}>
                    <span style={{ color: C.muted, fontFamily: "monospace", fontSize: 11 }}>{(t.entry_time || "—").slice(0, 5)}</span>
                    <span style={{ color: C.text, fontWeight: 700 }}>{t.symbol || "—"}</span>
                    <span style={{ color: t.direction === "Long" ? C.green : C.red, fontWeight: 700, fontSize: 11 }}>
                      {t.direction === "Long" ? "↑ L" : "↓ S"}
                    </span>
                    <span style={{ color: C.muted, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.setup_type ? t.setup_type.replace(/_/g, " ") : t.entry_signal || "—"}
                    </span>
                    <span style={{ color: net >= 0 ? C.green : C.red, fontWeight: 700, fontFamily: "monospace", textAlign: "right" }}>
                      {net >= 0 ? "+" : ""}{fmt$(net)}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {!data && <div style={{ color: C.muted, fontSize: 12, marginBottom: 14 }}>No trades logged.</div>}

      <div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Daily Note</div>
        {editingNote === day ? (
          <div style={{ display: "flex", gap: 8 }}>
            <textarea
              defaultValue={dayNotes[day] || ""}
              onChange={e => setDayNotes(prev => ({ ...prev, [day]: e.target.value }))}
              style={{ flex: 1, padding: 8, background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, minHeight: 60, fontFamily: "monospace" }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <button onClick={() => handleSaveNote(day, dayNotes[day])} style={{ padding: "6px 12px", background: C.green, color: "#000", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Save</button>
              <button onClick={() => setEditingNote(null)} style={{ padding: "6px 12px", background: C.border, color: C.text, border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11 }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div onClick={() => setEditingNote(day)} style={{ padding: 10, background: C.border + "20", border: `1px dashed ${C.border}`, borderRadius: 6, cursor: "pointer", minHeight: 48, color: dayNotes[day] ? C.text : C.muted, fontSize: 12, lineHeight: 1.4 }}>
            {dayNotes[day] || "+ Add note..."}
          </div>
        )}
      </div>
    </div>
  );
}

function getWorstHourSlot(detectorResults) {
  if (!detectorResults?.detectors) return null;
  const hc = detectorResults.detectors.find(d => d.id === "hour_cliff");
  if (!hc || hc.confidence === "insufficient") return null;
  return hc.details?.worst_slot?.slot || null;
}

function tradeInWorstSlot(trade, worstSlot) {
  if (!worstSlot || !trade.entry_time) return false;
  const [sH, sM] = worstSlot.split(":").map(Number);
  const [tH, tM] = String(trade.entry_time).split(":").map(Number);
  const start = sH * 60 + sM;
  const time  = tH * 60 + tM;
  return time >= start && time < start + 30;
}

export function MonthlyCalendar({ trades = [], getDailyNote, saveDailyNote, currentAccountId, pnlMode = "net", detectorResults = null }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay,  setSelectedDay]  = useState(null);
  const [dayNotes,     setDayNotes]     = useState({});
  const [editingNote,  setEditingNote]  = useState(null);
  const [metric,       setMetric]       = useState("pnl");

  useEffect(() => {
    const loadNotes = async () => {
      const dim = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
      const notes = {};
      for (let day = 1; day <= dim; day++) {
        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
        const note = await getDailyNote(date, currentAccountId);
        if (note) notes[day] = note.notes;
      }
      setDayNotes(notes);
    };
    loadNotes();
  }, [currentMonth, currentAccountId, getDailyNote]);

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDay    = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();

  const dayTradeMap = useMemo(() => {
    const map = {};
    for (const t of trades) {
      const rawDate = t.trade_date || t.created_at?.split("T")[0];
      if (!rawDate) continue;
      const d = new Date(rawDate + (rawDate.includes("T") ? "" : "T00:00:00"));
      if (d.getMonth() !== currentMonth.getMonth() || d.getFullYear() !== currentMonth.getFullYear()) continue;
      const day = d.getDate();
      if (!map[day]) map[day] = { trades: [], pnl: 0 };
      map[day].trades.push(t);
      map[day].pnl += tradeAmount(t, pnlMode);
    }
    return map;
  }, [trades, currentMonth, pnlMode]);

  const worstHourSlot = useMemo(() => getWorstHourSlot(detectorResults), [detectorResults]);

  const flaggedDays = useMemo(() => {
    if (!worstHourSlot) return new Set();
    const flagged = new Set();
    for (const [day, data] of Object.entries(dayTradeMap)) {
      if (data.trades.some(t => tradeInWorstSlot(t, worstHourSlot))) flagged.add(Number(day));
    }
    return flagged;
  }, [dayTradeMap, worstHourSlot]);

  const prevMonth = () => { setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)); setSelectedDay(null); };
  const nextMonth = () => { setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)); setSelectedDay(null); };

  const handleSaveNote = async (day, note) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    await saveDailyNote(date, currentAccountId, note);
    setDayNotes(prev => ({ ...prev, [day]: note }));
    setEditingNote(null);
  };

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

  const monthName    = currentMonth.toLocaleString("en-US", { month: "long", year: "numeric" });
  const currentMetricDef = METRICS.find(m => m.id === metric) || METRICS[0];

  return (
    <Card>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={prevMonth} style={{ background: "none", border: "none", color: C.blue, cursor: "pointer", fontSize: 14 }}>← Prev</button>
          <SectionTitle>{monthName} Calendar</SectionTitle>
          <button onClick={nextMonth} style={{ background: "none", border: "none", color: C.blue, cursor: "pointer", fontSize: 14 }}>Next →</button>
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {METRICS.map(m => (
            <button key={m.id} onClick={() => setMetric(m.id)} style={{
              padding: "4px 10px", borderRadius: 6, fontSize: 11,
              border: `1px solid ${metric === m.id ? C.blue : C.border}`,
              background: metric === m.id ? C.blue + "22" : "transparent",
              color: metric === m.id ? C.blue : C.muted,
              cursor: "pointer", fontWeight: 600,
            }}>{m.shortLabel}</button>
          ))}
        </div>
      </div>

      <div style={{ textAlign: "center", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: C.muted, marginBottom: 12 }}
        title="Days follow CME futures session conventions.">
        Session Days • {currentMetricDef.label}
        {worstHourSlot && <span style={{ color: C.yellow, marginLeft: 8 }}>⚠ = trades in worst slot ({worstHourSlot})</span>}
      </div>

      {/* Day headers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 8 }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: 10, color: C.muted, fontWeight: 600, padding: "4px 0" }}>{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
        {calendarDays.map((day, i) => {
          if (!day) return <div key={`empty-${i}`} />;
          const dayData    = dayTradeMap[day];
          const value      = getDayMetricValue(dayData, metric);
          const colors     = getSquareColor(value, metric);
          const hasNote    = !!dayNotes[day];
          const isFlagged  = flaggedDays.has(day);
          const isSelected = selectedDay === day;
          const formatted  = formatMetric(value, metric);

          return (
            <div key={day} onClick={() => setSelectedDay(isSelected ? null : day)} style={{
              padding: "6px 4px", borderRadius: 8,
              background: isSelected ? C.blue + "22" : colors.bg,
              border: `1px solid ${isSelected ? C.blue : colors.border}`,
              cursor: "pointer", minHeight: 70,
              display: "flex", flexDirection: "column", gap: 2,
              transition: "background 0.1s",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{day}</span>
                <span style={{ fontSize: 10 }}>{isFlagged && "⚠"}{hasNote && "📝"}</span>
              </div>
              {formatted != null && (
                <div style={{
                  fontSize: 11, fontWeight: 700, fontFamily: "monospace", lineHeight: 1.2,
                  color: metric === "pnl" ? (value >= 0 ? C.green : C.red)
                    : metric === "winrate" ? (value >= 0.6 ? C.green : value >= 0.4 ? C.yellow : C.red)
                    : C.text,
                }}>{formatted}</div>
              )}
              {dayData && metric !== "count" && (
                <div style={{ fontSize: 9, color: C.muted }}>{dayData.trades.length}t</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Streak panel */}
      <StreakPanel dayTradeMap={dayTradeMap} daysInMonth={daysInMonth} currentMonth={currentMonth} />

      {/* Day detail */}
      {selectedDay && (
        <DayDetail
          day={selectedDay}
          currentMonth={currentMonth}
          dayTradeMap={dayTradeMap}
          dayNotes={dayNotes}
          editingNote={editingNote}
          setEditingNote={setEditingNote}
          setDayNotes={setDayNotes}
          handleSaveNote={handleSaveNote}
        />
      )}
    </Card>
  );
}
